import type { Translations } from './ru'

export const enTranslations: Translations = {
  langSwitcher: {
    aria: 'Switch language',
  },

  nav: {
    calendar: 'Calendar',
    shiftCalendar: 'Shift calendar',
    timestamp: 'Timestamp',
    addSubtractDate: 'Add days',
    dateDifference: 'Date difference',
    businessDays: 'Business days',
    ageCalculator: 'Age',
    daysUntilDate: 'Days until',
    weekNumber: 'Week number',
    timeZones: 'Time zones',
    timer: 'Timer',
    pomodoro: 'Pomodoro',
    stopwatch: 'Stopwatch',
    blog: 'Blog',
  },

  common: {
    dateTools: 'Date tools',
    copy: 'Copy',
    copied: 'Copied',
    clear: 'Clear',
    localBadge: 'In browser',
    faq: 'Questions',
    updated: 'Updated',
    allBlogPosts: 'All blog posts',
    hotkeys: {
      label: 'Hotkeys',
      space: 'Space',
      startPause: 'start and pause',
      lap: 'lap',
      reset: 'reset',
    },
  },

  index: {
    title: 'Free tools for working with dates',
    description:
      'Fast local utilities for dates and time. No sign-up, no watermarks and no data sent to a server.',
    tools: [
      {
        title: 'Online calendar',
        description:
          'View a whole year or selected month, change the first day of the week and show ISO week numbers.',
        href: '/kalendar',
      },
      {
        title: 'Shift calendar',
        description:
          'Mark work and rest days for a 2/2, 3/3, one-on/three-off, or simple custom rotating schedule.',
        href: '/grafik-smen',
      },
      {
        title: 'Add or subtract days',
        description:
          'Add or subtract days, weeks, months and years from a selected date. The result shows the final date and weekday.',
        href: '/dobavit-dni-k-date',
      },
      {
        title: 'Date difference',
        description:
          'Find how many days, weeks, months and years passed between two dates. Includes an optional business-days count.',
        href: '/raznica-mezhdu-datami',
      },
      {
        title: 'Business days between dates',
        description:
          'Count working days using ordinary weekends or approved Russian federal five-day calendar data.',
        href: '/rabochie-dni-mezhdu-datami',
      },
      {
        title: 'Unix timestamp converter',
        description:
          'Convert Unix timestamps to dates and back. Current time, seconds, milliseconds, nanoseconds and epoch days.',
        href: '/timestamp',
      },
      {
        title: 'Age calculator',
        description:
          'Calculate age in years, months and days from a birth date. Includes total days lived.',
        href: '/kalkulyator-vozrasta',
      },
      {
        title: 'Days until date',
        description:
          'Choose a future or past date and instantly see days, weeks and status: today, past or upcoming.',
        href: '/skolko-dnej-do-daty',
      },
      {
        title: 'Week number',
        description:
          'Enter a date to get the ISO week, ISO year and weekday. Or enter an ISO year and week to see the date range.',
        href: '/nomer-nedeli',
      },
      {
        title: 'Time zone converter',
        description:
          'Compare one instant across 2–5 cities and IANA zones, including local date, UTC offset and daylight-saving transitions.',
        href: '/chasovye-poyasa',
      },
      {
        title: 'Timer',
        description:
          'Run a countdown with hours, minutes and seconds. The browser tab shows the remaining time.',
        href: '/timer',
      },
      {
        title: 'Pomodoro timer',
        description:
          'Alternate focus and break periods with 25/5, 50/10 or custom intervals. Settings stay in your browser.',
        href: '/pomodoro-tajmer',
      },
      {
        title: 'Stopwatch',
        description:
          'Count time upward with milliseconds displayed. Pause, reset, laps and splits are available.',
        href: '/sekundomer',
      },
    ],
  },

  calendar: {
    title: 'Online calendar',
    yearTitle: '{year} calendar',
    description:
      'Yearly and monthly calendar with first-day-of-week options, ISO week numbers and a print-friendly view.',
    yearDescription:
      'Calendar for {year} by month: choose a full-year or single-month view, the first day of the week, and ISO week numbers. Print it directly from your browser.',
    year: 'Year',
    month: 'Month',
    view: 'Calendar view',
    yearView: 'Full year',
    monthView: 'One month',
    weekStarts: 'Week starts',
    monday: 'Monday',
    sunday: 'Sunday',
    showIsoWeeks: 'Show ISO week numbers',
    isoWeekShort: 'Wk',
    isoNote:
      'ISO week numbers require a Monday-first grid. Enabling this option automatically switches the calendar to Monday.',
    disclaimer:
      'Only Saturdays and Sundays are highlighted. Public holidays and moved days off are not included.',
    print: 'Print',
    today: 'Current month',
    reset: 'Reset settings',
    weekdaysMonday: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    weekdaysSunday: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        question: 'Does this calendar include public holidays?',
        answer:
          'No. It highlights ordinary Saturdays and Sundays only. Public holidays, moved days off and official production calendars are outside this tool.',
      },
      {
        question: 'What do ISO week numbers mean?',
        answer:
          'ISO 8601 weeks start on Monday. The first ISO week contains the first Thursday in January, so dates near New Year can belong to the adjacent ISO year.',
      },
      {
        question: 'How do I print the calendar?',
        answer:
          'Choose the yearly or monthly view and select Print. Navigation and controls are hidden in the browser print layout.',
      },
    ],
    yearFaq: [
      {
        question: 'Can I view the {year} calendar month by month?',
        answer:
          'Yes. On the {year} calendar page, choose the one-month view and then select a month. You can also see all 12 months together.',
      },
      {
        question: 'Which weekends are marked in the {year} calendar?',
        answer:
          'Only Saturdays and Sundays in {year} are highlighted. Public holidays, moved days off, and official production calendars are not included.',
      },
      {
        question: 'How do I print the {year} calendar?',
        answer:
          'Choose the yearly or monthly view and select Print. Navigation and calendar controls are hidden in the browser print layout.',
      },
    ],
  },

  shiftCalendar: {
    title: 'Rotating shift calendar',
    description:
      'Choose a repeating cycle and its first work day to instantly mark your shifts and rest days for a full year.',
    presetTitle: 'Work pattern',
    presets: {
      '2_2': '2 work / 2 rest',
      '3_3': '3 work / 3 rest',
      '1_3': 'One on / three off',
      custom: 'Custom cycle',
    },
    customWorkDays: 'Consecutive work days',
    customRestDays: 'Consecutive rest days',
    firstWorkDate: 'First work day in the cycle',
    year: 'Calendar year',
    shiftHours: 'Hours in one shift',
    limits: 'Allowed: year 1900–2100, 1–31 work and rest days, and 1–24 hours per shift.',
    summaryTitle: 'Summary for {year}',
    workShifts: 'Work shifts',
    restDays: 'Rest days',
    totalHours: 'Hours at the selected duration',
    workDay: 'Work shift',
    restDay: 'Rest day',
    calendarTitle: 'Calendar for {year}',
    weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    disclaimerTitle: 'This is a personal repeating schedule',
    disclaimer:
      'The result is not a federal production calendar, timesheet, employer schedule, legal working-time norm, or pay calculation. Public holidays and moved days off do not alter the cycle automatically.',
    productionCalendarLink: 'Open the Russian production calendar as a reference',
    privacy:
      'Dates and schedule parameters are processed only in your browser, are not stored, and are not sent to analytics.',
    errors: {
      invalid_first_work_date: 'Enter a valid first work date.',
      unsupported_year: 'Choose a year from 1900 through 2100.',
      invalid_cycle: 'Set 1–31 work days and 1–31 rest days for a custom cycle.',
      invalid_shift_hours: 'Shift duration must be a whole number from 1 through 24 hours.',
    },
    howTitle: 'How to create a shift calendar',
    howTo: [
      'Choose 2/2, 3/3, one-on/three-off, or enter a simple custom cycle.',
      'Set the first work day in the cycle and the year to display.',
      'Set one shift duration for an indicative total in hours.',
      'Review work and rest days in the calendar; check public holidays separately in the production calendar.',
    ],
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        question: 'Do public holidays or moved days off change the cycle?',
        answer: 'No. The personal cycle continues independently of weekends, public holidays, and federal day-off transfers.',
      },
      {
        question: 'Can I use this as a timesheet or pay calculation?',
        answer: 'No. It is an indicative personal repeating calendar, not an employer document, legal hours audit, or pay calculation.',
      },
      {
        question: 'Where are my schedule dates sent?',
        answer: 'Nowhere. Calculation is local to your browser; dates, hours, and custom cycle values are excluded from analytics.',
      },
    ],
  },

  timestamp: {
    title: 'Unix timestamp converter',
    description:
      'Convert Unix timestamps to dates and back. Current time, seconds, milliseconds, nanoseconds and epoch days. Everything runs locally in your browser.',
    currentUnixTime: 'Current Unix time',
    seconds: 'Seconds',
    milliseconds: 'Milliseconds',
    nanoseconds: 'Nanoseconds',
    epochDays: 'Epoch days',
    timestampToDate: 'Timestamp -> date',
    dateToTimestamp: 'Date -> timestamp',
    inputPlaceholder: 'Enter a timestamp or epoch day',
    now: 'Now',
    detectedFormat: 'Detected format',
    secondsFormat: 'seconds',
    millisecondsFormat: 'milliseconds',
    nanosecondsFormat: 'nanoseconds',
    epochDaysFormat: 'epoch days',
    dateTime: 'Date and time',
    timezone: 'Time zone',
    copyAriaPrefix: 'Copy',
    faq: [
      {
        title: 'What is a Unix timestamp?',
        body:
          'A Unix timestamp is a time value counted from January 1, 1970 (UTC). APIs and databases often store it as seconds, milliseconds, nanoseconds or a day number from the Unix epoch.',
      },
      {
        title: 'How does the converter detect the timestamp format?',
        body:
          'The converter checks whether the value lands between years 2000 and 2100 as milliseconds, seconds, nanoseconds or epoch days. This covers current values from JavaScript, JVM, PostgreSQL and other systems.',
      },
      {
        title: 'When is this useful?',
        body:
          'Debugging APIs, working with databases, reading logs, configuring cron jobs and converting between time formats for programming. Everything runs locally, so data is not sent to a server.',
      },
    ],
  },

  addSubtractDate: {
    title: 'Add or subtract days from a date',
    description:
      'Add calendar or business days to a selected date. Business-day mode supports ordinary weekends or approved Russian federal calendar data.',
    calculationMode: 'Calculation type',
    calendarMode: 'Calendar period',
    workingMode: 'Business days',
    baseDate: 'Base date',
    direction: 'Direction',
    add: 'Add',
    subtract: 'Subtract',
    amount: 'Amount',
    unit: 'Unit',
    units: {
      days: 'days',
      weeks: 'weeks',
      months: 'months',
      years: 'years',
    },
    calendarBasis: 'Business-day calendar',
    weekendsOnlyBasis: 'Saturday and Sunday only',
    federalBasis: 'Russian federal five-day calendar',
    weekendsOnlyNote:
      'Monday through Friday are treated as working days. Public holidays and moved days off are not applied.',
    federalBasisNote:
      'Only approved Russian federal five-day data is used. The whole calculation is rejected when any year lacks coverage.',
    quickActions: 'Quick actions',
    presets: {
      week: '+7 days',
      'two-weeks-back': '-14 days',
      month: '+1 month',
    },
    resultReady: 'Final date',
    emptyResultTitle: 'Set a date and period',
    emptyResultDescription:
      'Choose the base date, direction and amount. The calculation runs locally in your browser.',
    workingEmptyTitle: 'Set a business-day period',
    workingEmptyDescription:
      'Choose a direction, business-day amount and calendar, then run one local calculation.',
    workingErrorEyebrow: 'Calculation not completed',
    workingErrorTitle: 'Check the calculation inputs',
    finalDate: 'Final date',
    weekday: 'Weekday',
    operation: 'Operation',
    added: 'Added',
    subtracted: 'Subtracted',
    workingExplanation:
      'The base date is not counted: the {amount}th business day was found after skipping {skipped} non-working days.',
    zeroWorkingExplanation:
      'The business-day amount is 0, so the base date is returned unchanged. No days were traversed.',
    skippedWeekends: 'Ordinary weekend days skipped',
    skippedFederal: 'Federal holidays and moved days skipped',
    weekendsOnlyGuardrail:
      'This mode handles Saturday and Sunday only and makes no public-holiday claim.',
    federalGuardrail:
      'Reference result for the Russian federal five-day week. Regions, six-day weeks, shifts, employer schedules, pay, and legal deadlines are excluded.',
    coverageYear: 'Russian federal calendar: {year}',
    coverageMetadata: 'Status: {status}. Checked {date}.',
    openCalendarYear: 'Open calendar year',
    noFederalFallback:
      'Partial results and an automatic fallback to ordinary weekends are prohibited.',
    useWeekendsOnly: 'Use ordinary weekends',
    monthEndNotice:
      'The original day does not exist in the target month, so the last day of that month is used.',
    localNote:
      'Dates are not saved and are not sent to a server. The calculation uses the browser local calendar date.',
    errors: {
      invalidAmount: 'Enter a whole number of 0 or more.',
      invalidWorkingAmount: 'Enter a whole number from 0 through 10,000.',
      invalidWorkingInput: 'Enter a valid date and a whole business-day amount from 0 through 10,000.',
      unsupportedDateRange: 'The calculation exceeds the supported date range of years 0001–9999.',
      coverageGap: 'No approved Russian federal calendar is available for: {years}.',
      processingFailure: 'The approved calendar is incomplete. The calculation stopped without a partial result.',
      invalidResult: 'Could not produce a valid final date.',
    },
    howToTitle: 'How to calculate a date',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        question: 'How are days and weeks added?',
        answer:
          'Days are counted as calendar days from the base date. Weeks are multiplied by 7 calendar days.',
      },
      {
        question: 'How do business days differ from calendar days?',
        answer:
          'Calendar mode counts every day. For a positive N, business-day mode does not count the base date; it returns the Nth working day before or after it and skips non-working days. When N = 0, the base date is returned unchanged.',
      },
      {
        question: 'Which holidays does federal mode include?',
        answer:
          'Only approved Russian federal holidays and moved days off for the standard five-day week. Crossing an uncovered year rejects the whole calculation without a partial answer or fallback.',
      },
      {
        question: 'What happens at the end of a month?',
        answer:
          'Months and years use a last-day-of-month rule: January 31 plus 1 month becomes February 28 or 29, not March.',
      },
      {
        question: 'Is time of day included?',
        answer:
          'No. The calculator works only with local calendar dates, without hours, minutes or time zones.',
      },
      {
        question: 'Is data sent to a server?',
        answer:
          'No. The calculation runs locally in the browser, and selected dates stay on your device.',
      },
    ],
    howTo: [
      'Choose the base date.',
      'Choose a calendar period or business days and the calculation direction.',
      'Enter an amount; for business days, choose ordinary weekends or approved Russian federal five-day data.',
      'Calendar mode recalculates the result immediately when an input changes.',
      'In business-day mode, select “Calculate business-day date”, then check the final date and weekday.',
    ],
    relatedTitle: 'Related tools',
    related: [
      {
        title: 'Business days between dates',
        description: 'Count working and non-working days in a range.',
        href: '/rabochie-dni-mezhdu-datami',
      },
      {
        title: 'Production calendar',
        description: 'Review coverage, sources and Russian federal five-day calendar data.',
        href: '/proizvodstvennyj-kalendar',
      },
      {
        title: 'Date difference',
        description: 'Calculate the duration between two dates.',
        href: '/raznica-mezhdu-datami',
      },
    ],
  },

  dateDifference: {
    title: 'Date difference in days and weeks',
    description:
      'Find how many days and weeks are between two dates, including total weeks, full weeks with remaining days, months and years.',
    startDate: 'Start date',
    endDate: 'End date',
    inclusive: 'Include end date',
    boundaryRuleExclusive:
      'After the dates are ordered, the earlier boundary is included and the later boundary is excluded.',
    boundaryRuleInclusive:
      'After the dates are ordered, both boundaries are included, adding one calendar day to the ordinary difference.',
    businessDaysOnly: 'Business days only',
    swap: 'Swap dates',
    quickRanges: 'Quick ranges',
    settings: 'Calculation settings',
    resultReady: 'Result updates instantly',
    emptyResultTitle: 'Choose two dates',
    emptyResultDescription:
      'Fill the fields manually or use a quick range. The result will appear here without reloading the page.',
    calendarDays: 'calendar days',
    timeline: 'Period timeline',
    approximationHint:
      'Months and years are approximate because calendar months have different lengths.',
    presetNextWeek: 'Next 7 days',
    presetNextWeekHint: 'Check the nearest deadline',
    presetWorkSprint: 'Work month',
    presetWorkSprintHint: '30 days with business days',
    presetPastWeek: 'Past week',
    presetPastWeekHint: 'Including today',
    swapped: 'Dates were swapped, so the absolute difference is shown.',
    totalWeeks: 'Total weeks',
    fullWeeksAndDays: 'Full weeks and remaining days',
    monthsApprox: 'Months (approx.)',
    yearsApprox: 'Years (approx.)',
    weekShort: 'w',
    dayShort: 'd',
    businessDays: 'Business days:',
    howToTitle: 'How to count days and weeks between dates',
    howTo: [
      'Choose the start and end dates.',
      'After ordering, the earlier date is always included. Optionally include the later date or enable the business-days count.',
      'Check total weeks, full weeks with remaining days, and the other results. They update immediately.',
    ],
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        question: 'How is the difference calculated?',
        answer:
          'The calculator orders the dates first. By default, the earlier date is included and the later date is excluded. Enabling "Include end date" counts both boundaries. Reversed input produces the same absolute difference.',
      },
      {
        question: 'How do I calculate how many weeks are between dates?',
        answer:
          'The calculator divides the final calendar-day count by 7. It shows total weeks with a decimal fraction and, separately, full weeks with remaining days. Both results use the same boundary rule as the day total.',
      },
      {
        question: 'What are business days?',
        answer:
          'Business days are Monday through Friday, excluding Saturday and Sunday. Public holidays are not included.',
      },
      {
        question: 'Why are months and years approximate?',
        answer:
          'Months have different lengths (28-31 days), so the month and year difference is an estimate based on the average number of days.',
      },
      {
        question: 'Is data sent to a server?',
        answer:
          'No. All calculations run locally in your browser. Dates stay on your device.',
      },
    ],
  },

  businessDays: {
    title: 'Business days between dates',
    description:
      'Count business days using ordinary weekends or the verified Russian federal calendar for a standard five-day workweek.',
    startDate: 'Start date',
    endDate: 'End date',
    includeEndDate: 'Include end date',
    includeEndDateHint:
      'Turn this off when you need the period up to a date, without counting the last day.',
    swap: 'Swap dates',
    quickRanges: 'Quick ranges',
    quickThisWeek: 'This week',
    quickThisWeekHint: 'Monday-Sunday of the current week',
    quickTwoWeeks: 'Next 14 days',
    quickTwoWeeksHint: 'Two weeks from today',
    quickMonth: 'Next 30 days',
    quickMonthHint: 'A working-month estimate from today',
    resultReady: 'Result updates instantly',
    emptyResultTitle: 'Choose two dates',
    emptyResultDescription:
      'Fill the start and end dates. Calendar days, weekdays and weekend days will appear here.',
    calendarDays: 'Calendar days',
    workingDays: 'Working days',
    workingDaysLower: 'working days',
    ordinaryWeekendDays: 'Ordinary weekend days',
    federalHolidayDays: 'Federal holidays / transfers',
    timeline: 'Period timeline',
    calendarMode: 'Calendar mode',
    weekendsOnlyMode: 'Saturday and Sunday only',
    federalMode: 'Russian federal calendar, five-day week',
    weekendsOnlyNote:
      'The calculator treats Monday through Friday as weekdays. Public holidays, moved days off and regional calendars are not included.',
    federalModeNote:
      'This mode uses approved Russian federal calendar data for the standard five-day week and requires complete coverage for every year in the range.',
    noHolidayGuarantee:
      'This is a reference weekends-only calculation. For payroll, vacation, contract deadlines and legal deadlines, check your employer calendar and officially published legislation.',
    federalGuardrail:
      'This reference calculation excludes regional holidays, six-day, shift and individual schedules. It is not a payroll, HR or legal-deadline guarantee; check your employer calendar, regional acts and official legislation.',
    coverageErrorEyebrow: 'No complete approved coverage',
    coverageErrorTitle: 'Federal mode cannot calculate this range',
    coverageErrorDescription:
      'Every year in the range needs verified data. No approved calendar is available for: {years}.',
    noFallback:
      'Partial results and automatic fallback to ordinary weekends are disabled to avoid an inaccurate answer.',
    useWeekendsOnly: 'Explicitly use Saturday and Sunday only',
    coverageYear: 'Coverage for {year}',
    coverageMetadata: 'Federal five-day week · status {status} · Verified {date}',
    openCalendarYear: 'Open year calendar →',
    productionCalendarTitle: 'Russian production calendar',
    productionCalendarDescription:
      'Review sources, federal holidays and transfers for published approved years of the standard five-day week.',
    swapped: 'Dates were swapped, so the absolute period is shown.',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        question: 'How are business days between dates counted?',
        answer:
          'The calculator walks through the selected calendar dates and treats Monday-Friday as weekdays. Saturday and Sunday are counted as weekend days.',
      },
      {
        question: 'Are public holidays and moved days off included?',
        answer:
          'Not by default: “Saturday and Sunday only” remains selected. The separate Russian federal mode includes approved holidays and transfers for a standard five-day week only when every year in the range is covered.',
      },
      {
        question: 'Is the end date included?',
        answer:
          'Yes by default. If you need the period up to a date without counting it, turn off “Include end date”.',
      },
      {
        question: 'Can I use this for payroll or legal deadlines?',
        answer:
          'No. This is a reference calculator without a guarantee for payroll, vacation, contract terms or legal deadlines.',
      },
      {
        question: 'Is data sent to a server?',
        answer:
          'No. All calculations run locally in your browser, and the selected dates are not sent to a server.',
      },
    ],
    howTo: [
      'Choose the start date.',
      'Choose the end date.',
      'Choose ordinary weekends or the Russian federal five-day calendar mode.',
      'Keep the end date option enabled or turn it off for an up-to-date calculation.',
      'Read calendar, working, ordinary weekend and federal holiday counts. The result updates immediately.',
    ],
    relatedTitle: 'Related date tools',
    related: [
      {
        title: 'Date difference',
        description: 'Count days, weeks, months and total duration between two dates.',
        href: '/raznica-mezhdu-datami',
      },
      {
        title: 'Days until date',
        description: 'Find how many calendar days remain until a selected date.',
        href: '/skolko-dnej-do-daty',
      },
      {
        title: 'Week number',
        description: 'Get the ISO week, ISO year and Monday-Sunday date range.',
        href: '/nomer-nedeli',
      },
    ],
  },

  ageCalculator: {
    title: 'Age calculator',
    description:
      'Calculate exact age from a birth date: full years, months, days and total days lived. The target date is optional, so today is used by default.',
    birthDate: 'Birth date',
    targetDate: 'Target date',
    targetDateHint: 'Optional: leave empty to calculate for today.',
    useToday: 'Today',
    hintTitle: 'How age is calculated',
    hintDescription:
      'The calculator counts full calendar years first, then months and remaining days between the birth date and target date.',
    resultReady: 'Result',
    emptyResultTitle: 'Enter a birth date',
    emptyResultDescription:
      'Enter a birth date and, if needed, a target date. The result appears immediately.',
    years: 'Years',
    months: 'Months',
    days: 'Days',
    period: 'Calculation period',
    totalDays: 'Total days',
    nextBirthday: 'Days to birthday',
    nextBirthdayHint: 'Next birthday: {date}.',
    relatedTitle: 'Related date tools',
    related: [
      {
        title: 'Date difference',
        description: 'Count days, weeks, months and business days between two dates.',
        href: '/raznica-mezhdu-datami',
      },
      {
        title: 'Unix timestamp converter',
        description: 'Convert a date to Unix timestamp or a timestamp back to a date.',
        href: '/timestamp',
      },
      {
        title: 'Stopwatch',
        description: 'Measure time with milliseconds directly in your browser.',
        href: '/sekundomer',
      },
    ],
    errors: {
      missing_birth_date: 'Enter a birth date.',
      invalid_birth_date: 'The birth date is invalid.',
      invalid_target_date: 'The target date is invalid.',
      future_birth_date: 'The birth date cannot be in the future.',
      future_target_date: 'The target date cannot be in the future.',
      target_before_birth: 'The target date cannot be earlier than the birth date.',
    },
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        question: 'How is age calculated?',
        answer:
          'The calculator counts full calendar years from the birth date first, then full months after the last birthday and the remaining days.',
      },
      {
        question: 'What does “total days” mean?',
        answer:
          'It is the number of calendar days between the birth date and target date. If no target date is provided, today is used.',
      },
      {
        question: 'Can I choose a future date?',
        answer:
          'No. The calculator shows an error for a future birth date or target date to avoid mixing current age with a forecast.',
      },
      {
        question: 'Is data sent to a server?',
        answer:
          'No. All calculations run locally in the browser, and dates stay on your device.',
      },
    ],
    howTo: [
      'Enter the birth date.',
      'Optionally enter a target date; leave it empty to use today.',
      'Immediately check the age in years, months and days, plus the total number of days lived.',
    ],
  },

  daysUntilDate: {
    title: 'Days until date',
    description:
      'Choose a date and see how many days and weeks remain. The calculator shows whether the date is today, in the past or upcoming.',
    targetDate: 'Target date',
    label: 'Event name',
    labelPlaceholder: 'For example: vacation, release, birthday',
    quickDates: 'Quick dates',
    quickWeek: 'In 7 days',
    quickMonth: 'In 30 days',
    quickYear: 'In one year',
    resultReady: 'Result updates instantly',
    emptyResultTitle: 'Choose a target date',
    emptyResultDescription:
      'The date and event name stay only in your browser. The result appears here without sending data to a server.',
    calendarDays: 'calendar days',
    daysRemaining: 'Days until date',
    weeksRemaining: 'Weeks until date',
    weeksApprox: 'Weeks (approx.)',
    status: 'Date status',
    statusFuture: 'Upcoming date',
    statusToday: 'Today',
    statusPast: 'Date has passed',
    timeline: 'Countdown timeline',
    today: 'Today',
    targetPrefix: 'Target date:',
    labelPrefix: 'Event',
    localNote:
      'The event name is not saved and is not sent to a server. It disappears after clearing or closing the tab.',
    weekShort: 'w',
    dayShort: 'd',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        question: 'How are days until a date calculated?',
        answer:
          'The calculator compares the selected date with today in your browser and shows the difference in calendar days.',
      },
      {
        question: 'What does date status mean?',
        answer:
          'A positive difference means the date is upcoming. Zero means today. A negative value means the selected date has already passed.',
      },
      {
        question: 'Is the event name saved?',
        answer:
          'No. The name is only used as a label on the page, is not saved in localStorage and is not sent to a server.',
      },
      {
        question: 'Are time zones included?',
        answer:
          'The calculation uses the browser local calendar date. Time of day is not used.',
      },
    ],
    howTo: [
      'Choose the target date in the calendar field.',
      'Optionally add an event name. It stays only on the page.',
      'Use a quick date preset or read the days, weeks and status immediately after entering a date.',
    ],
    relatedTitle: 'Related tools',
    related: [
      {
        title: 'Date difference',
        description: 'Compare two arbitrary dates and count business days.',
        href: '/raznica-mezhdu-datami',
      },
      {
        title: 'Timer',
        description: 'Run a countdown in hours, minutes and seconds.',
        href: '/timer',
      },
      {
        title: 'Unix timestamp converter',
        description: 'Convert date and time to Unix timestamp and back.',
        href: '/timestamp',
      },
    ],
  },

  weekNumber: {
    title: 'Week number',
    description:
      'Find the ISO week number for a date, including ISO year and weekday. You can also enter an ISO year and week number to get the Monday-to-Sunday date range.',
    dateLabel: 'Date',
    isoYearLabel: 'ISO year',
    isoWeekLabel: 'ISO week',
    dateDirectionTitle: 'Date → ISO week',
    rangeDirectionTitle: 'ISO week → date range',
    reactiveHint: 'The result updates as soon as the input is complete and valid.',
    copyDateToIso: 'Copy to ISO fields',
    copyIsoToDate: 'Set date to week Monday',
    today: 'Today',
    resultTitle: 'ISO week for date',
    weekUnit: 'ISO week',
    isoYear: 'ISO year',
    weekday: 'Weekday',
    isoWeekday: 'Day number',
    rangeTitle: 'Week range',
    weekStart: 'Mon',
    weekEnd: 'Sun',
    emptyDateTitle: 'Choose a date',
    emptyDateDescription:
      'Enter a date in the calendar field. The ISO week number, year and weekday will appear here.',
    emptyRangeDescription:
      'Enter an ISO year and a week from 1 to 52 or 53. The week range will appear here.',
    dateCopied: 'ISO year and week copied to the other block.',
    rangeCopied: 'Date set to the Monday of the selected ISO week.',
    invalidDate: 'Enter a valid date.',
    invalidIsoWeek: 'Enter an existing ISO week for the selected year.',
    newYearTitle: 'Why the ISO year can differ',
    newYearDescription:
      'An ISO week always starts on Monday, and the first ISO week of a year is the one with the first Thursday of January. That is why January 1 can belong to the last week of the previous ISO year, and December 31 can belong to the first week of the next one.',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        question: 'What is an ISO week number?',
        answer:
          'It is the week number under ISO 8601: weeks start on Monday, and week 1 is the week containing the first Thursday of January.',
      },
      {
        question: 'Why can a January date be in the previous ISO year?',
        answer:
          'If the first days of January fall before ISO week 1, they belong to the last ISO week of the previous year.',
      },
      {
        question: 'Can a year have 53 weeks?',
        answer:
          'Yes. Some ISO years have 53 weeks when the calendar layout creates one full extra ISO week.',
      },
      {
        question: 'Is data sent to a server?',
        answer:
          'No. Week number and date-range calculations run locally in your browser.',
      },
    ],
    howTo: [
      'Enter a date. Its ISO week, ISO year and weekday appear immediately.',
      'Separately enter an ISO year and week number. The Monday-Sunday range updates immediately.',
      'When needed, explicitly copy one block’s result into the other block’s inputs.',
    ],
    relatedTitle: 'Related tools',
    related: [
      {
        title: 'Date difference',
        description: 'Count days, weeks and business days between two dates.',
        href: '/raznica-mezhdu-datami',
      },
      {
        title: 'Days until date',
        description: 'Find how many days and weeks remain until a selected date.',
        href: '/skolko-dnej-do-daty',
      },
      {
        title: 'Unix timestamp converter',
        description: 'Convert date and time to Unix timestamp and back.',
        href: '/timestamp',
      },
    ],
  },

  timer: {
    title: 'Timer',
    description:
      'Run a timer with hours, minutes and seconds directly in your browser. No sign-up or app install.',
    hours: 'Hours',
    minutes: 'Minutes',
    seconds: 'Seconds',
    start: 'Start',
    pause: 'Pause',
    reset: 'Reset',
    quickAddLabel: 'Add time',
    quickAddMinuteShort: 'min',
    timerHint: 'Set a time and start the countdown.',
    doneNotice: 'Time is up. The finish sound has played.',
    invalidDuration: 'Enter a time greater than zero.',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        question: 'Do I need to sign up?',
        answer: 'No. Open the page, set a time and start the timer — no account is required.',
      },
      {
        question: 'Does the timer work without a server?',
        answer:
          'Yes. The countdown runs locally in your browser. The MVP uses no backend, history or reminders.',
      },
      {
        question: 'Will there be a sound when the timer ends?',
        answer:
          'Yes, the browser plays a short sound when the timer finishes. If browser or system audio is muted, you may not hear it.',
      },
      {
        question: 'Does the browser tab title update?',
        answer:
          'Yes. While the timer is running, the browser tab title shows the remaining time.',
      },
    ],
    howTo: [
      'Enter hours, minutes and seconds for the countdown.',
      'Use the +1, +5 or +10 minute quick buttons when needed.',
      'Click “Start”: the countdown starts immediately, and the browser tab title shows the remaining time.',
      'Use “Pause” and “Reset”. The timer plays a short sound at the end.',
    ],
    relatedTitle: 'Related tools',
    related: [
      { title: 'Pomodoro timer', description: 'Alternate focus and breaks with intervals.', href: '/pomodoro-tajmer' },
      { title: 'Stopwatch', description: 'Count upward and save lap markers.', href: '/sekundomer' },
    ],
  },

  pomodoro: {
    title: 'Pomodoro timer',
    description:
      'A local interval timer for focused work: 25/5, 50/10 or custom focus and break durations. No account and no data sent to a server.',
    focus: 'Focus',
    break: 'Break',
    status: {
      idle: 'Ready',
      running: 'Running',
      paused: 'Paused',
      completed: 'Phase completed',
    },
    cycles: 'Focus sessions completed',
    start: 'Start',
    resume: 'Resume',
    pause: 'Pause',
    reset: 'Reset',
    presets: 'Preset intervals',
    custom: 'Custom durations',
    focusMinutes: 'Focus, minutes',
    breakMinutes: 'Break, minutes',
    alerts: 'Completion alerts',
    sound: 'Sound',
    vibration: 'Vibration',
    enableNotifications: 'Allow browser notifications',
    disableNotifications: 'Disable browser notifications',
    notificationsEnabled: 'Notifications enabled.',
    notificationsDisabled: 'Notifications disabled.',
    notificationsDenied: 'The browser did not allow notifications.',
    notificationsUnavailable: 'This browser does not support notifications.',
    notificationTitle: 'Pomodoro phase completed',
    alertsHint: 'Every alert is opt-in. Notification permission is requested only from this button and no notifications appear without permission.',
    accuracyHint: 'The timer reconciles against an absolute deadline when you return to the tab.',
    focusDone: 'Focus completed. Your break is ready — start it when you are ready.',
    breakDone: 'Break completed. Your next focus session is ready.',
    invalidDuration: 'Enter whole durations: focus 1–180 minutes and break 1–60 minutes.',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        question: 'How does the Pomodoro technique work?',
        answer: 'Choose a focus interval and a break. When a phase ends, the timer prepares the next one but does not start it automatically.',
      },
      {
        question: 'Will the timer keep time in a background tab?',
        answer: 'Yes. Remaining time is calculated from an absolute deadline and reconciled when you return. Mobile browser suspension or device sleep can still delay sound and notifications, so this is not an alarm-clock replacement.',
      },
      {
        question: 'What is stored in the browser?',
        answer: 'Only durations and alert preferences. The active countdown, deadline and cycle history are not stored.',
      },
      {
        question: 'Do I need an account or server?',
        answer: 'No. The timer runs locally in your browser, with no sign-up, tasks, statistics or settings sent to a server.',
      },
    ],
    howTo: [
      'Choose the 25/5 or 50/10 preset, or enter custom durations.',
      'Optionally opt in to sound, vibration or browser notifications.',
      'Click “Start” for a focus session; pause and resume keep the same interval.',
      'When focus ends, manually start the prepared break.',
    ],
    relatedTitle: 'Related tools',
    related: [
      { title: 'Regular timer', description: 'Run a one-off countdown.', href: '/timer' },
      { title: 'Stopwatch', description: 'Count upward and save lap markers.', href: '/sekundomer' },
    ],
  },

  stopwatch: {
    title: 'Online stopwatch with milliseconds',
    description:
      'Start or pause the stopwatch, reset it, and record laps with split times. Milliseconds are displayed directly in your browser.',
    start: 'Start',
    pause: 'Pause',
    reset: 'Reset',
    lap: 'Lap',
    lapsTitle: 'Lap markers',
    total: 'Total',
    split: 'Split',
    copyLaps: 'Copy laps',
    copyFailed: 'Copy failed',
    hint: 'Start and Pause control the count, Lap saves a marker, and Reset begins a new session.',
    accuracyTitle: 'How stopwatch accuracy works',
    accuracyDescription:
      'Time is measured with the browser monotonic clock, while the display refreshes through animation frames. A background tab may refresh slowly or stop drawing; when you return, the value is recalculated from the browser clock. Device sleep and browser-specific limits are not guaranteed, so this is not a laboratory measuring instrument.',
    howTitle: 'How to use the stopwatch',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        question: 'Does the stopwatch run locally?',
        answer:
          'Yes. The counter runs in your browser, with no backend and no data sent to a server.',
      },
      {
        question: 'Are milliseconds shown?',
        answer:
          'Yes. The main display and browser tab title show hours, minutes, seconds and milliseconds. This is a display format, not a promise of laboratory precision.',
      },
      {
        question: 'What happens in a background tab?',
        answer:
          'The browser may slow or stop display updates. When you return, the stopwatch recalculates the value from the monotonic browser clock; device sleep and browser limits can still affect the result.',
      },
      {
        question: 'Can I pause the stopwatch?',
        answer:
          'Yes. Use “Pause” to stop counting and “Start” to continue from the same value.',
      },
      {
        question: 'Can I save laps?',
        answer:
          'Yes. Click “Lap” to record the current time and see the split from the previous marker.',
      },
      {
        question: 'Do I need to sign up?',
        answer:
          'No. The stopwatch is available as soon as you open the page.',
      },
    ],
    howTo: [
      'Click “Start” to begin counting with milliseconds displayed.',
      'Click “Lap” to save the total time and the split from the previous marker.',
      'Click “Pause” to stop counting, then “Start” to continue from the same value.',
      'Click “Reset” to clear the time and laps and begin a new session.',
    ],
    relatedTitle: 'Related tools',
    related: [
      { title: 'Pomodoro timer', description: 'Alternate focus and breaks with intervals.', href: '/pomodoro-tajmer' },
      { title: 'Regular timer', description: 'Run a one-off countdown.', href: '/timer' },
    ],
  },

  timeZones: {
    title: 'Time zone converter',
    description:
      'Set a date, time and source IANA zone to compare one exact instant across 2–5 cities. The calculation includes UTC offsets and daylight-saving transitions.',
    sourceDate: 'Source date',
    sourceTime: 'Source time',
    sourceZone: 'Source time zone',
    comparisonTitle: 'Time in selected zones',
    comparisonHint: 'Every row represents the same exact instant.',
    copyLink: 'Copy share link',
    addZone: 'Add a city or IANA zone',
    searchPlaceholder: 'For example, New York or America/New_York',
    searchHint: 'Search supports Russian and English names for popular cities. The IANA ID remains the source of truth; ambiguous CST and IST inputs are rejected.',
    add: 'Add',
    remove: 'Remove zone',
    moveUp: 'Move up',
    moveDown: 'Move down',
    gapTitle: 'This local time does not exist.',
    gapDescription: 'The UTC offset changed at this point, so this local time does not exist (for example, during a daylight-saving transition). Choose a time before or after the transition.',
    invalidInput: 'Check the date, time and IANA zone.',
    foldTitle: 'This time occurs twice',
    foldDescription: 'When clocks move back, two exact instants share the same local time. Choose the intended occurrence.',
    earlier: 'First occurrence (earlier)',
    later: 'Second occurrence (later)',
    howTitle: 'How to compare time zones',
    howTo: [
      'Choose the source date, time and IANA time zone.',
      'Add two to five cities or zones for comparison.',
      'If the time is in an autumn DST fold, choose the first or second occurrence.',
      'Check the local date, time and UTC offset in every row or copy a share link.',
    ],
    accuracyTitle: 'Where time zone rules come from',
    accuracyDescription:
      'The converter runs locally using the browser time-zone database. It applies known DST rules but may lag behind the latest political changes, so the result is not a promise of permanent official accuracy.',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        question: 'Does the converter account for daylight saving time?',
        answer: 'Yes. The offset is resolved for the selected date. A nonexistent spring time is rejected, and an autumn repeated time offers an explicit first-or-second choice.',
      },
      {
        question: 'Can I enter CST or IST?',
        answer: 'No. Those abbreviations are ambiguous. Use an IANA zone such as America/Chicago or Asia/Kolkata.',
      },
      {
        question: 'Is data sent to a server?',
        answer: 'No. All calculations run in the browser. A share link contains only the selected date, time and IANA zones.',
      },
      {
        question: 'Why did the date change in another zone?',
        answer: 'The same instant can fall on the previous or next calendar day depending on the selected zone UTC offset.',
      },
    ],
  },

  blog: {
    breadcrumb: 'Blog',
    title: 'dateify blog',
    description:
      'Practical notes about dates, deadlines, timestamps, business days and local browser calculations.',
    languageAria: 'Article language',
    postLanguageAria: 'Article language',
    empty: 'No posts in this language yet.',
    notFoundTitle: 'Post not found',
    notFoundDescription: 'This dateify blog post does not exist or the link is outdated.',
    relatedTools: 'Related tools',
    relatedToolDescription: {
      timestamp: 'Convert Unix timestamps to dates and back.',
      dateDifference: 'Days, weeks, months and business days between dates.',
      timeZones: 'Compare dates and times across 2–5 cities with DST handling.',
    },
    localeName: {
      ru: 'Русский',
      en: 'English',
    },
  },

  seo: {
    index: {
      title: 'Free online date tools — dateify.ru',
      description:
        'Free local tools for dates and time: Unix timestamp converter, date difference calculator and other date utilities. No sign-up and no server upload.',
    },
    calendar: {
      title: 'Online yearly and monthly calendar — dateify.ru',
      description:
        'Free online yearly or monthly calendar with Monday or Sunday week start, ISO week numbers and print layout. Weekends only, with no public holidays.',
    },
    calendar2026: {
      title: '2026 calendar by month, printable — dateify.ru',
      description:
        '2026 calendar by month with Monday or Sunday week start, ISO week numbers and print layout. Only Saturday and Sunday weekends are highlighted.',
    },
    calendar2027: {
      title: '2027 calendar by month, printable — dateify.ru',
      description:
        '2027 calendar by month with Monday or Sunday week start, ISO week numbers and print layout. Only Saturday and Sunday weekends are highlighted.',
    },
    shiftCalendar: {
      title: '2/2, 3/3 and rotating shift calendar — dateify.ru',
      description:
        'Build a yearly shift calendar for 2/2, 3/3, one-on/three-off or a custom cycle. Shifts, rest days and hours are calculated locally in your browser.',
    },
    timestamp: {
      title: 'Unix timestamp converter online — dateify.ru',
      description:
        'Convert Unix timestamps to dates and back. Current Unix time, seconds, milliseconds, nanoseconds and epoch days. Free, no sign-up, local in your browser.',
    },
    addSubtractDate: {
      title: 'Add or subtract days from a date online — dateify.ru',
      description:
        'Add calendar or business days to a date using ordinary weekends or approved Russian federal calendar data. Final date and weekday stay local in your browser.',
    },
    dateDifference: {
      title: 'How many days and weeks between dates — online calculator — dateify.ru',
      description:
        'Calculate days and weeks between two dates, including total weeks, full weeks with remaining days, months and years. Free and local in your browser.',
    },
    businessDays: {
      title: 'Business days between dates calculator — dateify.ru',
      description:
        'Count business days using ordinary weekends or approved Russian federal five-day calendar data. Regional and individual schedules are excluded.',
    },
    ageCalculator: {
      title: 'Age calculator online — years, months and days — dateify.ru',
      description:
        'Calculate age from a birth date: full years, months, days and total days lived. Free browser-local age calculator with no sign-up.',
    },
    daysUntilDate: {
      title: 'Days until date calculator — countdown in days and weeks — dateify.ru',
      description:
        'Calculate how many days and weeks remain until a selected date. Shows whether the date is today, already past or upcoming. Free and local in your browser.',
    },
    weekNumber: {
      title: 'Week number calculator — ISO week by date — dateify.ru',
      description:
        'Find the ISO week number, ISO year and weekday for a date, or get the date range from an ISO year and week number. Free and local in your browser.',
    },
    timeZones: {
      title: 'Time zone converter online — compare cities — dateify.ru',
      description:
        'Compare exact date and time across 2–5 IANA time zones. Handles DST transitions, runs locally in the browser and supports shareable links.',
    },
    timer: {
      title: 'Online timer — free, in browser — dateify.ru',
      description:
        'Run a timer with hours, minutes and seconds directly in your browser. Free, no sign-up, backend or app install.',
    },
    pomodoro: {
      title: 'Pomodoro timer online — 25/5 and 50/10 — dateify.ru',
      description:
        'Free Pomodoro focus timer with 25/5, 50/10 and custom intervals. Runs locally in your browser with no sign-up.',
    },
    stopwatch: {
      title: 'Online stopwatch with milliseconds — dateify.ru',
      description:
        'Online stopwatch with milliseconds displayed, start and pause, reset, laps and split times. Free and local in your browser.',
    },
    blog: {
      title: 'dateify blog - notes about dates and time',
      description:
        'Practical notes about date calculations, Unix timestamps, deadlines, business days and local browser date/time tools.',
    },
  },
}
